import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { STATISTICS } from "@/constants/testIds";
import { toast } from "sonner";
import {
  BarChart3, FileSpreadsheet, FileText, RefreshCw, Users2,
  TrendingUp, MessagesSquare, Tag, Zap, ArrowRight, Info, LogIn, UserPlus,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend, AreaChart, Area,
} from "recharts";
import HintIcon from "@/components/HintIcon";

const PH_ORANGE = "#EA580C";
const PH_INDIGO = "#4F46E5";
const PH_EMERALD = "#059669";
const PH_ROSE = "#E11D48";
const PH_SLATE = "#0F172A";
const PH_AMBER = "#D97706";

const LABEL_PALETTE = {
  // Manual palette — matches Label admin colors
  slate: "#64748B", indigo: "#4F46E5", orange: "#EA580C", red: "#DC2626",
  emerald: "#059669", purple: "#8B5CF6", amber: "#D97706", cyan: "#0891B2",
  // v22.4 — AI-generated tag palette (backend uses these for un-registered slugs)
  pink: "#DB2777", teal: "#0D9488", lime: "#65A30D", fuchsia: "#C026D3",
  rose: "#E11D48", sky: "#0284C7", violet: "#7C3AED", yellow: "#CA8A04",
};

const RANGE_OPTIONS = [
  { value: 1, label: "Last 24h" },
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

const SERIES_RANGES = [
  { value: "today", label: "Today" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
  { value: "all", label: "All" },
];

function TimeSeriesCard({
  testId, title, endpoint, color = "#EA580C", icon: Icon, defaultRange = "daily",
  showGeography = false, series = null, height = 240,
}) {
  const nav = useNavigate();
  const [range, setRange] = useState(() => localStorage.getItem(`${testId}:range`) || defaultRange);
  const [chartType, setChartType] = useState(() => localStorage.getItem(`${testId}:type`) || "line");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    localStorage.setItem(`${testId}:range`, range);
    localStorage.setItem(`${testId}:type`, chartType);
  }, [range, chartType, testId]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(endpoint, { params: { range } }).then((r) => {
      if (alive) setData(r.data);
    }).catch(() => {}).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [endpoint, range]);

  const buckets = data?.buckets || [];
  const topCities = data?.top_cities || [];
  // Resolve the series to render
  const seriesToRender = series || [{ key: "count", name: title, color }];
  // Compute total: single-series → total; multi-series → sum first series
  const total = data?.total ?? buckets.reduce((s, b) => s + (b[seriesToRender[0].key] || 0), 0);
  const avg = data?.avg_per_bucket ?? (buckets.length ? round2(total / buckets.length) : 0);
  const totalsBreakdown = data?.totals || null;

  return (
    <div
      data-testid={testId}
      className="bg-white border border-slate-200 rounded-lg p-5"
    >
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-slate-600" />}
          <div className="font-heading font-bold tracking-tight">{title}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden bg-slate-50" role="tablist">
            {SERIES_RANGES.map((r) => (
              <button
                key={r.value}
                data-testid={`${testId}-range-${r.value}`}
                onClick={() => setRange(r.value)}
                className={`text-[10.5px] font-semibold px-2 py-1 transition-colors duration-150 ${
                  range === r.value ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden bg-slate-50">
            {["line", "bar"].map((ct) => (
              <button
                key={ct}
                data-testid={`${testId}-type-${ct}`}
                onClick={() => setChartType(ct)}
                className={`text-[10.5px] font-semibold px-2 py-1 uppercase tracking-widest ${
                  chartType === ct ? "bg-orange-600 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {ct}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-baseline gap-4 mb-3 flex-wrap">
        {totalsBreakdown ? (
          <div className="flex items-baseline gap-4">
            {Object.entries(totalsBreakdown).map(([k, v]) => {
              const s = seriesToRender.find((x) => x.key === k);
              return (
                <div key={k}>
                  <div className="font-heading font-extrabold text-2xl tabular leading-none" style={{ color: s?.color || "#0F172A" }}>{fmt(v)}</div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mt-0.5">{s?.name || k}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <>
            <div className="font-heading font-extrabold text-3xl tabular leading-none">{fmt(total)}</div>
            <div className="text-[11px] text-slate-500">
              total · avg <span className="font-semibold tabular">{avg}</span> per {data?.bucket || "period"}
            </div>
          </>
        )}
        {loading && <div className="text-[10px] text-slate-400 uppercase tracking-widest ml-auto">loading…</div>}
      </div>

      <div className={showGeography && topCities.length > 0 ? "grid grid-cols-1 lg:grid-cols-3 gap-4" : ""}>
        <div className={showGeography && topCities.length > 0 ? "lg:col-span-2" : ""} style={{ width: "100%", height }}>
          <ResponsiveContainer>
            {chartType === "line" ? (
              <AreaChart data={buckets} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  {seriesToRender.map((s, i) => (
                    <linearGradient key={s.key} id={`grad-${testId}-${i}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={s.color} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={s.color} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 10, fill: "#64748B" }} allowDecimals={false} />
                <Tooltip content={<TooltipBox />} />
                {seriesToRender.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
                {seriesToRender.map((s, i) => (
                  <Area
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.name}
                    stroke={s.color}
                    fill={`url(#grad-${testId}-${i})`}
                    strokeWidth={2}
                    strokeDasharray={s.style === "dashed" ? "4 3" : undefined}
                  />
                ))}
              </AreaChart>
            ) : (
              // In bar mode, drop periods where every series is zero — this
              // stops a 30-day range with only 1-2 populated days from
              // showing dozens of empty slots and shrinking the visible bars
              // to <10px each. Line mode keeps the full time range so trends
              // remain readable.
              (() => {
                const nonEmpty = (buckets || []).filter((b) =>
                  seriesToRender.some((s) => (Number(b[s.key]) || 0) > 0)
                );
                const barData = nonEmpty.length > 0 ? nonEmpty : buckets;
                return (
                  <BarChart data={barData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }} barCategoryGap="20%" barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748B" }} />
                    <YAxis tick={{ fontSize: 10, fill: "#64748B" }} allowDecimals={false} />
                    <Tooltip content={<TooltipBox />} cursor={{ fill: "rgba(15, 23, 42, 0.04)" }} />
                    {seriesToRender.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
                    {seriesToRender.map((s) => (
                      <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={[4, 4, 0, 0]} maxBarSize={80} />
                    ))}
                  </BarChart>
                );
              })()
            )}
          </ResponsiveContainer>
        </div>

        {showGeography && topCities.length > 0 && (
          <div data-testid={`${testId}-cities`} className="lg:col-span-1 border-l lg:border-slate-100 lg:pl-4">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">Top cities in range</div>
            <div className="space-y-1.5">
              {topCities.slice(0, 8).map((c, idx) => {
                const pct = total ? Math.round((c.count / total) * 100) : 0;
                return (
                  <button
                    key={c.name}
                    data-testid={`${testId}-city-${idx}`}
                    onClick={() => nav(`/users?city=${encodeURIComponent(c.name)}`)}
                    className="w-full text-left group"
                    title={`See users from ${c.name}`}
                  >
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-700 group-hover:text-orange-700 truncate">{c.name}</span>
                      <span className="font-mono tabular text-slate-600 group-hover:text-orange-700">{c.count}</span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-slate-100 mt-0.5 overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-300 group-hover:opacity-90" style={{ width: `${pct}%`, background: color }} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function LabelDistributionCard({ testId, defaultRange = "daily" }) {
  const nav = useNavigate();
  const [range, setRange] = useState(() => localStorage.getItem(`${testId}:range`) || defaultRange);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { localStorage.setItem(`${testId}:range`, range); }, [range, testId]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/statistics/labels", { params: { range } }).then((r) => {
      if (alive) setData(r.data);
    }).catch(() => {}).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [range]);

  const labels = (data?.labels || []).slice(0, 8).map((l) => ({
    ...l,
    fill: LABEL_PALETTE[l.color] || "#64748B",
  }));

  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-slate-600" />
          <div className="font-heading font-bold tracking-tight">Label distribution</div>
        </div>
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden bg-slate-50">
          {SERIES_RANGES.map((r) => (
            <button
              key={r.value}
              data-testid={`${testId}-range-${r.value}`}
              onClick={() => setRange(r.value)}
              className={`text-[10.5px] font-semibold px-2 py-1 transition-colors duration-150 ${
                range === r.value ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="text-[11px] text-slate-500 mb-3 flex items-center gap-3">
        <span><b className="text-slate-800 font-mono tabular">{fmt(data?.total || 0)}</b> total tags in range</span>
        {loading && <span className="text-slate-400 uppercase tracking-widest">loading…</span>}
      </div>

      {labels.length === 0 ? (
        <div className="text-sm text-slate-500 py-10 text-center border border-dashed border-slate-200 rounded-md">
          No labels applied within this range.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={labels}
                  dataKey="count"
                  nameKey="name"
                  innerRadius={45}
                  outerRadius={90}
                  paddingAngle={2}
                  onClick={(d) => d?.slug && nav(`/users?tag=${encodeURIComponent(d.slug)}`)}
                  cursor="pointer"
                >
                  {labels.map((l, i) => <Cell key={i} fill={l.fill} />)}
                </Pie>
                <Tooltip content={<TooltipBox />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1">
            {labels.map((l) => (
              <button
                key={l.slug}
                data-testid={`${testId}-row-${l.slug}`}
                onClick={() => nav(`/users?tag=${encodeURIComponent(l.slug)}`)}
                className="w-full flex items-center justify-between text-left px-2 py-1.5 rounded hover:bg-slate-50 group"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: l.fill }} />
                  <span className="text-sm text-slate-700 group-hover:text-orange-700 truncate">{l.name}</span>
                  {l.is_ai_generated && (
                    <span
                      title="AI-generated label — not in your master Labels list. Consider promoting it if this pattern is useful."
                      className="inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border bg-slate-100 border-slate-200 text-slate-500 shrink-0"
                    >
                      AI
                    </span>
                  )}
                </div>
                <span className="font-mono tabular text-sm text-slate-600 group-hover:text-orange-700 shrink-0 ml-2">{l.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function fmt(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString();
}

function KPI({ testId, label, value, sub, tone = "slate", icon: Icon, to, hoverCue }) {
  const tones = {
    slate: "border-slate-200 bg-white text-slate-900",
    orange: "border-orange-200 bg-orange-50 text-orange-800",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-800",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
  };
  const nav = useNavigate();
  const clickable = Boolean(to);
  const interact = clickable
    ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-150 group"
    : "";
  return (
    <div
      data-testid={testId}
      onClick={clickable ? () => nav(to) : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter") nav(to); } : undefined}
      title={clickable ? (hoverCue || "Click to filter Users") : undefined}
      className={`relative border rounded-lg p-4 flex flex-col gap-1 ${tones[tone]} ${interact}`}
    >
      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest opacity-80">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className="font-heading font-extrabold text-3xl tabular leading-tight">{value}</div>
      {sub && <div className="text-[11px] opacity-70">{sub}</div>}
      {clickable && (
        <div className="absolute right-2 bottom-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex items-center gap-0.5 text-[9.5px] font-semibold uppercase tracking-widest">
          <span>Filter</span>
          <ArrowRight className="h-3 w-3" />
        </div>
      )}
    </div>
  );
}

function Card({ title, subtitle, hint, right, testId, children }) {
  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-heading font-bold text-base tracking-tight text-slate-900">{title}</h3>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5 max-w-lg">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {right}
          <HintIcon hint={hint} testId={testId ? `${testId}-hint` : undefined} />
        </div>
      </div>
      {children}
    </div>
  );
}

function TooltipBox({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-slate-200 bg-white shadow px-3 py-2 text-xs">
      <div className="font-semibold text-slate-900 mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-sm" style={{ background: p.color }} />
          <span className="text-slate-600">{p.name}</span>
          <span className="ml-auto font-mono tabular text-slate-900">{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

async function downloadFile(path, filename) {
  const token = localStorage.getItem("phc_token");
  const resp = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function Statistics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [overview, aiUsage] = await Promise.all([
        api.get(`/statistics/overview?days=${days}`),
        // AI usage is admin-only; ignore 403 for viewers/agents
        api.get(`/ai-usage/summary?days=${days}`).catch(() => ({ data: null })),
      ]);
      setData(overview.data);
      setUsage(aiUsage.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load statistics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const funnelData = useMemo(() => {
    if (!data) return [];
    const f = data.funnel;
    return [
      { name: "Day 1", value: f.day1, fill: "#FDBA74", bucket: "day1" },
      { name: "Day 2", value: f.day2, fill: "#FB923C", bucket: "day2" },
      { name: "Day 3", value: f.day3, fill: "#EA580C", bucket: "day3" },
      { name: "Active", value: f.active, fill: PH_EMERALD, bucket: "active" },
      { name: "Inactive", value: f.inactive, fill: "#94A3B8", bucket: "inactive" },
      { name: "Paid", value: f.paid || 0, fill: "#4F46E5", bucket: "paid" },
    ];
  }, [data]);

  const labelData = useMemo(() => {
    if (!data) return [];
    return data.labels.slice(0, 8).map((l) => ({
      name: l.name,
      slug: l.slug,
      value: l.count,
      fill: LABEL_PALETTE[l.color] || "#64748B",
    }));
  }, [data]);

  const dailyData = useMemo(() => (data?.daily || []).map((d) => ({
    ...d,
    date_short: d.date.slice(5), // MM-DD
  })), [data]);

  const doExport = async (kind) => {
    setExporting(kind);
    try {
      if (kind === "xlsx") {
        await downloadFile(`/exports/statistics.xlsx?days=${days}`, `statistics_${days}d.xlsx`);
      } else {
        await downloadFile(`/exports/statistics.pdf?days=${days}`, `statistics_${days}d.pdf`);
      }
      toast.success(`Exported ${kind.toUpperCase()}`);
    } catch (e) {
      toast.error(`Export failed: ${e.message || e}`);
    } finally {
      setExporting("");
    }
  };

  if (loading && !data) {
    return <div className="p-10 text-sm text-slate-500">Loading statistics…</div>;
  }
  if (!data) return null;

  const t = data.totals;

  return (
    <div data-testid={STATISTICS.root} className="p-8 lg:p-12 max-w-[1280px]">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-1">Reports</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-indigo-600" /> Statistics
            <HintIcon
              testId="statistics-page-hint"
              hint={
                <>
                  End-to-end view of trial conversions, message activity, label spread and AI token usage.
                  Every chart runs entirely on your Mongo data — <b>even when AI capabilities are turned off</b>.<br /><br />
                  Every card here has its own <Info className="inline h-3 w-3 mx-0.5 text-slate-400" /> icon
                  with a plain-English explanation. Tap it any time.
                </>
              }
            />
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            data-testid={STATISTICS.daysSelect}
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="px-3 py-2 text-xs border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none min-w-[140px]"
          >
            {RANGE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <button
            data-testid={STATISTICS.refreshBtn}
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-60"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            data-testid={STATISTICS.exportXlsx}
            onClick={() => doExport("xlsx")}
            disabled={exporting === "xlsx"}
            className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md bg-emerald-600 text-white font-semibold hover:bg-emerald-500 disabled:opacity-60"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" />
            {exporting === "xlsx" ? "Exporting…" : "Excel"}
          </button>
          <button
            data-testid={STATISTICS.exportPdf}
            onClick={() => doExport("pdf")}
            disabled={exporting === "pdf"}
            className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md bg-rose-600 text-white font-semibold hover:bg-rose-500 disabled:opacity-60"
          >
            <FileText className="h-3.5 w-3.5" />
            {exporting === "pdf" ? "Exporting…" : "PDF"}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <KPI testId={STATISTICS.cardTotal} label="Total users" value={fmt(t.total)} icon={Users2} to="/users" hoverCue="See all users" />
        <KPI testId={STATISTICS.cardPaid} label="Paid" value={fmt(t.paid)} tone="emerald" icon={TrendingUp} to="/users?bucket=paid" hoverCue="See paid users" />
        <KPI testId={STATISTICS.cardTrial} label="Trial" value={fmt(t.trial)} tone="orange" to="/users?status=trial" hoverCue="See trial users" />
        <KPI testId={STATISTICS.cardConversion} label="Conversion" value={`${t.conversion_rate}%`} tone="indigo" sub={`${t.paid}/${t.total}`} to="/users?bucket=paid" hoverCue="See converted users" />
        <KPI testId={STATISTICS.cardDnd} label="DND" value={fmt(t.dnd)} tone="rose" to="/users?dnd=true" hoverCue="See DND users" />
        <KPI testId={STATISTICS.cardAiPaused} label="AI paused" value={fmt(t.ai_paused)} tone="amber" to="/users?ai_paused=true" hoverCue="See AI-paused users" />
      </div>

      {/* Signups & Logins — interactive time-series with range + line/bar toggle */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <TimeSeriesCard
          testId="statistics-signups-series"
          title="Signups"
          endpoint="/statistics/signups"
          color={PH_ORANGE}
          icon={UserPlus}
          showGeography={true}
        />
        <TimeSeriesCard
          testId="statistics-logins-series"
          title="Logins"
          endpoint="/statistics/logins"
          color={PH_INDIGO}
          icon={LogIn}
        />
      </div>

      {/* Signups & Conversions + Message activity — with range switcher and Line/Bar toggle */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <TimeSeriesCard
          testId={STATISTICS.chartSignups}
          title="Signups & Conversions"
          endpoint="/statistics/conversions"
          series={[
            { key: "signups", name: "Signups", color: PH_ORANGE },
            { key: "conversions", name: "Conversions", color: PH_EMERALD },
          ]}
          icon={TrendingUp}
        />
        <TimeSeriesCard
          testId={STATISTICS.chartMessages}
          title="Message activity"
          endpoint="/statistics/messages"
          series={[
            { key: "inbound", name: "Inbound", color: PH_INDIGO },
            { key: "outbound", name: "Outbound", color: PH_SLATE },
            { key: "ai", name: "AI-authored", color: PH_ORANGE, style: "dashed" },
            { key: "template", name: "Template", color: "#0f766e", style: "dashed" },
          ]}
          icon={MessagesSquare}
        />
      </div>

      {/* Funnel + Labels distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card
          testId={STATISTICS.chartFunnel}
          title="Bucket funnel"
          hint={
            <>
              How current users are distributed across trial buckets and post-trial states.<br /><br />
              <b>Day 1 / Day 2 / Day 3</b> — trial users grouped by hours since signup (0–24 / 24–48 / 48–72).<br />
              <b className="text-emerald-700">Active</b> — past the 72h trial, not paid yet, but opened the app within the last 7 days. High-priority for win-back.<br />
              <b className="text-slate-700">Inactive</b> — past 72h trial, not paid, no login for 7+ days. Cold leads.<br />
              <b className="text-indigo-700">Paid</b> — converted subscribers.
            </>
          }
          right={<Users2 className="h-4 w-4 text-orange-500" />}
        >
          <div className="text-[10px] text-slate-500 mb-1 flex items-center gap-1">
            <ArrowRight className="h-3 w-3" /> Click any bar to open Users filtered by that bucket
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={funnelData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }} barCategoryGap="12%">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 10, fill: "#64748B" }} allowDecimals={false} />
                <Tooltip content={<TooltipBox />} cursor={{ fill: "rgba(15, 23, 42, 0.04)" }} />
                <Bar
                  dataKey="value"
                  name="Users"
                  radius={[6, 6, 0, 0]}
                  maxBarSize={216}
                  cursor="pointer"
                  onClick={(entry) => {
                    const b = entry?.payload?.bucket || entry?.bucket;
                    if (b) navigate(`/users?bucket=${encodeURIComponent(b)}`);
                  }}
                >
                  {funnelData.map((entry, idx) => (
                    <Cell
                      key={idx}
                      fill={entry.fill}
                      data-testid={`statistics-funnel-cell-${entry.bucket}`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {funnelData.map((entry) => (
              <button
                key={entry.bucket}
                data-testid={`statistics-funnel-chip-${entry.bucket}`}
                onClick={() => navigate(`/users?bucket=${encodeURIComponent(entry.bucket)}`)}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-md border border-slate-200 bg-white hover:border-orange-300 hover:bg-orange-50 hover:text-orange-800 transition-colors duration-150 group"
                title={`Show ${entry.name} users`}
              >
                <span className="h-2 w-2 rounded-sm" style={{ background: entry.fill }} />
                <span>{entry.name}</span>
                <span className="font-mono tabular text-slate-500 group-hover:text-orange-700">{fmt(entry.value)}</span>
                <ArrowRight className="h-3 w-3 text-slate-300 group-hover:text-orange-600" />
              </button>
            ))}
          </div>
        </Card>

        <LabelDistributionCard testId={STATISTICS.chartLabels} />
      </div>

      {/* AI Usage (admin-only) */}
      {usage && (
        <Card
          testId={STATISTICS.chartAIUsage}
          title="AI usage"
          hint={
            <>
              Token spend across capabilities and connectors.<br /><br />
              <b>Requests</b> — number of LLM calls made.<br />
              <b>Input / Output tokens</b> — what you&apos;re billed on. Emergent Universal Key uses a 4-chars/token estimate;
              direct providers (Anthropic, OpenAI) return exact counts.<br />
              <b>By capability</b> — which micro-task (draft_message, score_user, generate_digest, assistant_query) is burning the most.<br />
              <b>By connector</b> — which of your configured connectors is being routed to.
            </>
          }
          right={<Zap className="h-4 w-4 text-orange-500" />}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {[
              ["Requests", usage.totals.requests, "bg-orange-50 border-orange-200 text-orange-700"],
              ["Input tokens", usage.totals.input_tokens, "bg-emerald-50 border-emerald-200 text-emerald-700"],
              ["Output tokens", usage.totals.output_tokens, "bg-indigo-50 border-indigo-200 text-indigo-700"],
              ["Total tokens", usage.totals.total_tokens, "bg-slate-100 border-slate-300 text-slate-800"],
            ].map(([lbl, val, cls]) => (
              <div key={lbl} className={`border rounded-md p-3 ${cls}`}>
                <div className="text-[10px] font-semibold uppercase tracking-widest opacity-80">{lbl}</div>
                <div className="font-heading font-extrabold text-2xl tabular mt-1">{fmt(val)}</div>
              </div>
            ))}
          </div>

          <div style={{ width: "100%", height: 240 }} className="mb-6">
            <ResponsiveContainer>
              {(() => {
                // Filter empty days so the few populated days get thick, readable bars.
                const nonEmpty = (usage.daily || []).filter(
                  (d) => (Number(d.input_tokens) || 0) > 0 || (Number(d.output_tokens) || 0) > 0
                );
                const barData = nonEmpty.length > 0 ? nonEmpty : (usage.daily || []);
                return (
                  <BarChart data={barData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }} barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#64748B" }} tickFormatter={(d) => (d || "").slice(5)} />
                    <YAxis tick={{ fontSize: 10, fill: "#64748B" }} allowDecimals={false} />
                    <Tooltip content={<TooltipBox />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="input_tokens" name="Input" stackId="tok" fill={PH_EMERALD} radius={[0, 0, 0, 0]} maxBarSize={80} />
                    <Bar dataKey="output_tokens" name="Output" stackId="tok" fill={PH_INDIGO} radius={[4, 4, 0, 0]} maxBarSize={80} />
                  </BarChart>
                );
              })()}
            </ResponsiveContainer>
          </div>

          {usage.by_capability?.length > 0 && (
            <>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">By capability</div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
                {usage.by_capability.map((b) => (
                  <div key={b.capability} className="border border-slate-200 rounded-md p-3">
                    <div className="text-xs font-semibold text-slate-800">{b.capability}</div>
                    <div className="text-[10px] text-slate-500 mt-1">
                      {fmt(b.requests)} calls · {fmt(b.input_tokens + b.output_tokens)} tokens
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          {usage.by_connector?.length > 0 && (
            <>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">By connector</div>
              <div className="overflow-hidden border border-slate-200 rounded-md">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Connector</th>
                      <th className="text-right px-3 py-2 font-semibold">Requests</th>
                      <th className="text-right px-3 py-2 font-semibold">Input</th>
                      <th className="text-right px-3 py-2 font-semibold">Output</th>
                      <th className="text-right px-3 py-2 font-semibold">Avg latency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usage.by_connector.map((cn) => (
                      <tr key={cn.connector_id} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-semibold">{cn.connector_name}</td>
                        <td className="px-3 py-2 text-right font-mono tabular">{fmt(cn.requests)}</td>
                        <td className="px-3 py-2 text-right font-mono tabular">{fmt(cn.input_tokens)}</td>
                        <td className="px-3 py-2 text-right font-mono tabular">{fmt(cn.output_tokens)}</td>
                        <td className="px-3 py-2 text-right font-mono tabular text-slate-600">{cn.avg_latency_ms}ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {(!usage.by_connector?.length && !usage.by_capability?.length) && (
            <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-md">
              No AI calls yet in the selected window.
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
